/**
 * Medea Park leave entitlements that Xero cannot infer on its own.
 *
 * Pure functions: the caller supplies roster facts; nothing here reads a
 * database or a clock. Every rule cites the registered agreement text that
 * was checked against the page images on 23 Sep 2026:
 *
 *   General Staff EA 2024 (AE529357)
 *     cl 14(b)(i)  shiftworker +38 h annual leave, qualifying tests (I)/(II);
 *                  Annexure A undertaking item 2 defines "weekend" as ordinary
 *                  work on a Saturday and/or Sunday in any one calendar week.
 *     cl 14(d)     public holidays and annual leave (FT rostered: one day per
 *                  holiday; PT rostered: only when normally rostered; not for
 *                  a Saturday/Sunday holiday or one paid at the PH rate by
 *                  agreement).
 *     cl 6         day worker = ordinary hours 06:00–18:00 Monday–Friday.
 *   Nurses EA 2024 (AE530670)
 *     Annexure A undertaking 1: regular ordinary work outside
 *                  07:00–19:00 Monday–Friday; cl 23.3 +3.8 h/month engaged as
 *                  a shift worker.
 *     cl 23.4/23.5 one additional day per public holiday for shift workers
 *                  (non-rotating part-timers only when rostered that day).
 *     cl 31.4      no extra day where, by agreement, the holiday was worked at
 *                  double time and a half or not worked and paid single time.
 *     cl 24.12     part-time personal leave: 114 h (20–<30 h/wk), 152 h
 *                  (≥ 30 h/wk) on the preceding three months' average; below
 *                  20 h/wk the general pro-rata rule (cl 24.6) applies.
 *     cl 7         day worker = ordinary hours 07:00–19:00 Monday–Friday.
 *
 * Interpretation decisions (Paul Benson, 23 Sep 2026):
 *   - GS +38 h: (I) qualifiers accrue progressively at 190 h/yr on the Xero
 *     annual-leave template; (II)-only qualifiers are credited pro-rata once
 *     they reach the 10th qualifying weekend, with a leave-year-end true-up.
 *   - Nurses +3.8 h/month: pro-rata by ordinary hours, i.e. 197.6 h/yr on the
 *     template for shift-worker months. Superseded 2 Oct 2026: nurse PT monthly
 *     pro-rata is ADOPTED_INTERPRETATION (Paul; provider confirmation advisable),
 *     implemented only in Slice B; Slice A additional amounts are review-only.
 *   - Public-holiday credit = the worker's ordinary daily hours (rostered
 *     hours that day for a part-timer credited under the rostered-day rule).
 *   - Leave year = the worker's service year.
 */

export type LeaveAgreement = 'GENERAL_STAFF' | 'NURSES';

/** One rostered or worked ordinary-time shift, in tenant-local time. */
export interface OrdinaryShiftFact {
  /** Local calendar date the shift starts on, YYYY-MM-DD. */
  date: string;
  /** Minutes after local midnight of `date` the shift starts. */
  startMinute: number;
  /** Minutes after local midnight of `date` the shift ends (may exceed 1440). */
  endMinute: number;
  /** Ordinary (non-overtime) minutes paid for the shift. */
  ordinaryMinutes: number;
  /** Approved paid minutes by local date, when elapsed and civil durations differ. */
  ordinaryDateMinutes?: readonly { date: string; minutes: number }[];
}

export const FULL_TIME_ANNUAL_ORDINARY_HOURS = 1976;
export const BASE_ANNUAL_LEAVE_HOURS = 152;
/** GS cl 14(b)(i). */
export const GS_SHIFTWORKER_ADDITIONAL_HOURS = 38;
/** Nurses cl 23.3: 3.8 h × 12 months. */
export const NURSES_SHIFTWORKER_ADDITIONAL_HOURS = 45.6;
/** GS Annexure A undertaking 2: more than four ordinary hours per weekend. */
export const GS_WEEKEND_SHIFT_MIN_ORDINARY_MINUTES = 240;
/** GS cl 14(b)(i)(II): "10 or more weekend shifts during any one leave year". */
export const GS_QUALIFYING_WEEKENDS = 10;

/**
 * "Regularly" is not defined in either agreement. Statura reads it as: over
 * the trailing 13 weeks, the worker worked in at least 4 weeks and the
 * pattern appears in at least half of the weeks worked. Tenant-configurable
 * in the caller; flagged in the verification artifact for provider review.
 */
export interface RegularityPolicy {
  lookbackWeeks: number;
  minimumWorkedWeeks: number;
  minimumShareOfWorkedWeeks: number;
}

export const DEFAULT_REGULARITY_POLICY: RegularityPolicy = {
  lookbackWeeks: 13,
  minimumWorkedWeeks: 4,
  minimumShareOfWorkedWeeks: 0.5,
};

const DAY_WORKER_WINDOW: Record<LeaveAgreement, { start: number; end: number }> = {
  GENERAL_STAFF: { start: 6 * 60, end: 18 * 60 },
  NURSES: { start: 7 * 60, end: 19 * 60 },
};

function parseDate(date: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) throw new Error(`leave_entitlement_invalid_date:${date}`);
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
}

/** Calendar date of a UTC-midnight value built by parseDate (pure date arithmetic, no timezone). */
function isoDate(value: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${value.getUTCFullYear()}-${pad(value.getUTCMonth() + 1)}-${pad(value.getUTCDate())}`;
}

/** 0 = Sunday … 6 = Saturday, for a calendar date (no timezone drift). */
export function dayOfWeek(date: string): number {
  return parseDate(date).getUTCDay();
}

export function addDays(date: string, days: number): string {
  const value = parseDate(date);
  value.setUTCDate(value.getUTCDate() + days);
  return isoDate(value);
}

/** Monday of the calendar week containing `date` (ISO week, Monday start). */
export function weekStart(date: string): string {
  const dow = dayOfWeek(date);
  return addDays(date, -((dow + 6) % 7));
}

/**
 * Minutes of a shift that fall outside the agreement's day-worker window.
 * Any minute on a Saturday or Sunday is outside it.
 */
export function minutesOutsideDayWorkerWindow(
  shift: OrdinaryShiftFact,
  agreement: LeaveAgreement,
): number {
  const window = DAY_WORKER_WINDOW[agreement];
  let outside = 0;
  // Work in civil-day spans; the historical planner evaluates many qualifying dates.
  for (let at = shift.startMinute; at < shift.endMinute; ) {
    const dayOffset = Math.floor(at / 1440);
    const dayEnd = Math.min(shift.endMinute, (dayOffset + 1) * 1440);
    const dow = dayOfWeek(addDays(shift.date, dayOffset));
    const length = dayEnd - at;
    const inside =
      dow === 0 || dow === 6
        ? 0
        : Math.max(
            0,
            Math.min(dayEnd, dayOffset * 1440 + window.end) -
              Math.max(at, dayOffset * 1440 + window.start),
          );
    outside += length - inside;
    at = dayEnd;
  }
  return outside;
}

function shiftsInLookback(
  shifts: readonly OrdinaryShiftFact[],
  asOf: string,
  lookbackWeeks: number,
): OrdinaryShiftFact[] {
  const from = addDays(asOf, -7 * lookbackWeeks);
  return shifts.filter(
    (shift) => shift.date > from && shift.date <= asOf && shift.ordinaryMinutes > 0,
  );
}

export interface RegularityVerdict {
  qualifies: boolean;
  workedWeeks: number;
  matchingWeeks: number;
}

function weeklyRegularity(
  shifts: readonly OrdinaryShiftFact[],
  asOf: string,
  policy: RegularityPolicy,
  weekMatches: (weekShifts: OrdinaryShiftFact[]) => boolean,
): RegularityVerdict {
  const byWeek = new Map<string, OrdinaryShiftFact[]>();
  for (const shift of shiftsInLookback(shifts, asOf, policy.lookbackWeeks)) {
    const key = weekStart(shift.date);
    byWeek.set(key, [...(byWeek.get(key) ?? []), shift]);
  }
  const workedWeeks = byWeek.size;
  let matchingWeeks = 0;
  for (const weekShifts of byWeek.values()) if (weekMatches(weekShifts)) matchingWeeks += 1;
  const qualifies =
    workedWeeks >= policy.minimumWorkedWeeks &&
    matchingWeeks >= policy.minimumShareOfWorkedWeeks * workedWeeks;
  return { qualifies, workedWeeks, matchingWeeks };
}

/**
 * GS cl 14(b)(i)(I): regularly rostered to work ordinary hours outside the
 * day-worker window (06:00–18:00 Monday–Friday).
 */
export function gsQualifiesByRosterPattern(
  shifts: readonly OrdinaryShiftFact[],
  asOf: string,
  policy: RegularityPolicy = DEFAULT_REGULARITY_POLICY,
): RegularityVerdict {
  return weeklyRegularity(shifts, asOf, policy, (weekShifts) =>
    weekShifts.some((shift) => minutesOutsideDayWorkerWindow(shift, 'GENERAL_STAFF') > 0),
  );
}

/**
 * GS Annexure A undertaking 2 is operative: actual ordinary work exceeding
 * four hours aggregated across Saturday/Sunday in a Monday-start calendar week.
 * Count once; exclude overtime and unpaid breaks. Inclusive is comparison only.
 */
export function gsQualifyingWeekendCount(
  shifts: readonly OrdinaryShiftFact[],
  leaveYearStart: string,
  leaveYearEndExclusive: string,
  threshold: 'AT_LEAST_FOUR' | 'MORE_THAN_FOUR' = 'MORE_THAN_FOUR',
): number {
  return gsQualifyingWeekendDates(shifts, leaveYearStart, leaveYearEndExclusive, threshold).length;
}

/** Calendar weekends count once, combining Saturday and Sunday ordinary time. */

const WEEKEND_MINUTE_SCALE = 1_000_000;

function weekendQualifies(scaledMinutes: number, threshold: 'AT_LEAST_FOUR' | 'MORE_THAN_FOUR') {
  const limit = GS_WEEKEND_SHIFT_MIN_ORDINARY_MINUTES * WEEKEND_MINUTE_SCALE;
  return threshold === 'AT_LEAST_FOUR' ? scaledMinutes >= limit : scaledMinutes > limit;
}

export function gsQualifyingWeekendDates(
  shifts: readonly OrdinaryShiftFact[],
  leaveYearStart: string,
  leaveYearEndExclusive: string,
  threshold: 'AT_LEAST_FOUR' | 'MORE_THAN_FOUR',
): string[] {
  const weeks = new Map<string, { minutes: number; date: string }>();
  // Adopted software convention: Saturday owns a boundary weekend; flag it in history evidence.
  for (const shift of [...shifts].sort((a, b) => a.date.localeCompare(b.date))) {
    const duration = shift.endMinute - shift.startMinute;
    if (duration <= 0) continue;
    for (let at = shift.startMinute; at < shift.endMinute; ) {
      const offset = Math.floor(at / 1440);
      const dayEnd = Math.min(shift.endMinute, (offset + 1) * 1440);
      const date = addDays(shift.date, offset);
      const dow = dayOfWeek(date);
      if (dow === 0 || dow === 6) {
        const saturday = dow === 6 ? date : addDays(date, -1);
        if (saturday >= leaveYearStart && saturday < leaveYearEndExclusive) {
          const entry = weeks.get(saturday) ?? { minutes: 0, date };
          const before = entry.minutes;
          // Accumulate in integer micro-minutes so float error cannot push an exact
          // four-hour weekend over the strict "more than four hours" threshold.
          entry.minutes += Math.round(
            (shift.ordinaryDateMinutes
              ? (shift.ordinaryDateMinutes.find((d) => d.date === date)?.minutes ?? 0)
              : (shift.ordinaryMinutes * (dayEnd - at)) / duration) * WEEKEND_MINUTE_SCALE,
          );
          const qualifies = (minutes: number) => weekendQualifies(minutes, threshold);
          if (!qualifies(before) && qualifies(entry.minutes)) entry.date = date;
          weeks.set(saturday, entry);
        }
      }
      at = dayEnd;
    }
  }
  return [...weeks.values()]
    .filter((w) => weekendQualifies(w.minutes, threshold))
    .map((w) => w.date)
    .sort();
}

/**
 * Nurses Annexure A undertaking 1: regularly rostered ordinary hours outside
 * 07:00–19:00 Monday–Friday. No seven-day coverage or weekend requirement.
 * Slice A uses approved ordinary work as corroboration under the adopted
 * trailing-13-week rule; roster-based regularity/engagement is Slice B.
 */
export function nursesIsShiftWorker(
  shifts: readonly OrdinaryShiftFact[],
  asOf: string,
  policy: RegularityPolicy = DEFAULT_REGULARITY_POLICY,
): RegularityVerdict & { daysOfWeekCovered: number; borderline: boolean } {
  const recent = shiftsInLookback(shifts, asOf, policy.lookbackWeeks);
  const regularity = weeklyRegularity(shifts, asOf, policy, (weekShifts) =>
    weekShifts.some((shift) => minutesOutsideDayWorkerWindow(shift, 'NURSES') > 0),
  );
  return {
    ...regularity,
    daysOfWeekCovered: new Set(recent.map((shift) => dayOfWeek(shift.date))).size,
    borderline:
      regularity.workedWeeks === policy.minimumWorkedWeeks ||
      (regularity.workedWeeks > 0 &&
        regularity.matchingWeeks === policy.minimumShareOfWorkedWeeks * regularity.workedWeeks),
  };
}

/**
 * Annual-leave hours per full-time year to set on the Xero template (which
 * accrues relative to ordinary earnings, so part-timers accrue pro rata).
 */
export function annualLeaveTemplateHours(input: {
  agreement: LeaveAgreement;
  shiftWorker: boolean;
}): number {
  if (!input.shiftWorker) return BASE_ANNUAL_LEAVE_HOURS;
  return input.agreement === 'GENERAL_STAFF'
    ? BASE_ANNUAL_LEAVE_HOURS + GS_SHIFTWORKER_ADDITIONAL_HOURS
    : BASE_ANNUAL_LEAVE_HOURS + NURSES_SHIFTWORKER_ADDITIONAL_HOURS;
}

function round4(value: number): number {
  return Math.round(value * 10_000) / 10_000;
}

/**
 * GS (II)-only qualifier: the additional 38 h is due pro rata to ordinary
 * hours worked in the leave year once the 10th qualifying weekend is reached.
 * Returns the hours to credit now (never negative; the year-end call with the
 * full year's ordinary hours is the true-up). Workers who qualify under (I)
 * accrue it through the 190 h template instead and get 0 here.
 */
export function gsWeekendRouteCreditDue(input: {
  qualifiesByRosterPattern: boolean;
  qualifyingWeekends: number;
  ordinaryHoursInLeaveYear: number;
  alreadyCreditedHours: number;
}): number {
  if (input.qualifiesByRosterPattern) return 0;
  if (input.qualifyingWeekends < GS_QUALIFYING_WEEKENDS) return 0;
  const due = Math.min(
    GS_SHIFTWORKER_ADDITIONAL_HOURS,
    (GS_SHIFTWORKER_ADDITIONAL_HOURS * input.ordinaryHoursInLeaveYear) /
      FULL_TIME_ANNUAL_ORDINARY_HOURS,
  );
  return Math.max(0, round4(due - input.alreadyCreditedHours));
}

/**
 * Nurses cl 24.12: part-time personal leave per year from the preceding three
 * months' average weekly hours (or engaged hours with < 3 months' service).
 * Below 20 h/wk the clause is silent and the general progressive rule
 * (cl 24.6, 152 h per full-time 38 h week) applies pro rata.
 */
export function nursesPartTimePersonalLeaveAnnualHours(averageWeeklyHours: number): {
  annualHours: number;
  basis: 'CL_24_12_A' | 'CL_24_12_B' | 'CL_24_6_PRO_RATA';
} {
  if (averageWeeklyHours >= 30) return { annualHours: 152, basis: 'CL_24_12_B' };
  if (averageWeeklyHours >= 20) return { annualHours: 114, basis: 'CL_24_12_A' };
  return {
    annualHours: round4((152 * Math.max(0, averageWeeklyHours)) / 38),
    basis: 'CL_24_6_PRO_RATA',
  };
}

/** Xero FIXEDAMOUNTEACHPERIOD stores the annual figure; this is the per-fortnight equivalent. */
export function perFortnight(annualHours: number): number {
  return round4(annualHours / 26);
}

export type PublicHolidayPaymentByAgreement =
  | 'NONE'
  /** Worked and paid at the public-holiday rate by agreement (GS cl 35; Nurses cl 31.4(a) 2.5×). */
  | 'WORKED_AT_PH_RATE'
  /** Not worked and paid single time by agreement (Nurses cl 31.4(b)). */
  | 'NOT_WORKED_PAID_SINGLE_TIME';

export interface PublicHolidayCreditInput {
  agreement: LeaveAgreement;
  employment: 'FULL_TIME' | 'PART_TIME' | 'CASUAL';
  /** GS: works to a roster (not a day worker). Nurses: cl 23.3 shift worker. */
  rosteredOrShiftWorker: boolean;
  /** Nurses cl 23.5: part-time shift worker whose place on the roster does not rotate. */
  nonRotatingRoster?: boolean;
  holidayDate: string;
  /** Hours the worker was rostered on the holiday (0 when not rostered). */
  rosteredHoursOnHoliday: number;
  /** The worker's ordinary daily hours (8.0 for a shift worker whose paid meal counts). */
  ordinaryDailyHours: number;
  paymentByAgreement: PublicHolidayPaymentByAgreement;
}

export interface PublicHolidayCreditVerdict {
  creditHours: number;
  reason:
    | 'CREDITED'
    | 'CASUAL'
    | 'NOT_ROSTERED_OR_SHIFT_WORKER'
    | 'WEEKEND_HOLIDAY_GS'
    | 'PAID_AT_PH_RATE_BY_AGREEMENT'
    | 'PAID_SINGLE_TIME_BY_AGREEMENT'
    | 'PART_TIME_NOT_ROSTERED_THAT_DAY';
  clause: string;
}

/** One additional day of annual leave for a public holiday, where the agreement gives it. */
export function publicHolidayAnnualLeaveCredit(
  input: PublicHolidayCreditInput,
): PublicHolidayCreditVerdict {
  const gs = input.agreement === 'GENERAL_STAFF';
  const clause = gs ? 'GS cl 14(d)(ii)' : 'Nurses cl 23.4–23.5, 31.4';
  if (input.employment === 'CASUAL') return { creditHours: 0, reason: 'CASUAL', clause };
  if (!input.rosteredOrShiftWorker) {
    return { creditHours: 0, reason: 'NOT_ROSTERED_OR_SHIFT_WORKER', clause };
  }
  const dow = dayOfWeek(input.holidayDate);
  if (gs && (dow === 0 || dow === 6)) {
    return { creditHours: 0, reason: 'WEEKEND_HOLIDAY_GS', clause };
  }
  if (input.paymentByAgreement === 'WORKED_AT_PH_RATE') {
    return { creditHours: 0, reason: 'PAID_AT_PH_RATE_BY_AGREEMENT', clause };
  }
  if (!gs && input.paymentByAgreement === 'NOT_WORKED_PAID_SINGLE_TIME') {
    return { creditHours: 0, reason: 'PAID_SINGLE_TIME_BY_AGREEMENT', clause };
  }
  const partTimeRosteredDayRule =
    input.employment === 'PART_TIME' && (gs || input.nonRotatingRoster === true);
  if (partTimeRosteredDayRule) {
    if (input.rosteredHoursOnHoliday <= 0) {
      return { creditHours: 0, reason: 'PART_TIME_NOT_ROSTERED_THAT_DAY', clause };
    }
    return { creditHours: round4(input.rosteredHoursOnHoliday), reason: 'CREDITED', clause };
  }
  return { creditHours: round4(input.ordinaryDailyHours), reason: 'CREDITED', clause };
}
